import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { When } from 'react-if';
import './styles.css';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ReactComponent as ModalLogo } from './modal-logo.svg';
import { createPaymentIntents, createTransaction } from '../../../../firebase/api/stripe';
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { useAuth } from '../../../../hooks/useAuth';
import { sendEmail } from '../../../../firebase/api/mail';
import {
    getReceiveMoneyEmailTemplate,
    getReceiveMoneyWithoutStripeAccountEmailTemplate,
} from '../../../../templates';
import { getStripeConnectLink } from '../../../../constants/stripe';
import { updateUsersAfterPayment } from '../../../../firebase/api/users';
import { updateReview } from '../../../../firebase/api/review';
import { nanoid } from 'nanoid';
import { addTotalDonation } from '../../../../firebase/api/post';

const donationAmounts = [5, 10, 15, 25, 50];

const DEFAULT_STRIPE_ACC_ID = 'DEFAULT_STRIPE_ACC_ID';

const DEFAULT_FEE = 40;

const cardInputStyles = {
    base: {
        color: '#464a55',
        fontSize: '16px',
        fontFamily: '"Nunito", sans-serif',
        fontWeight: 700,
        '::placeholder': {
            color: '#626161',
            fontWeight: 400,
        },
    },
    invalid: {
        color: '#e5424d',
        ':focus': {
            color: '#303238',
        },
    },
};

const MakePaymentModal = ({
    postId,
    isOpen,
    recipientFirstName,
    recipientId,
    recipientEmail,
    recipientStripeUserId,
    reviewId,
    onToggle,
    onPaySuccess,
}) => {
    const [isDisabled, setDisabledStatus] = useState(false);
    const [error, setError] = useState(null);
    const stripe = useStripe();
    const elements = useElements();
    const {
        currentUser: { uid: senderId, firstName: senderFirstName },
    } = useAuth();

    const [feeAmount, setFeeAmount] = useState(DEFAULT_FEE);
    const [selectedDonationAmounts, setSelectedDonationAmounts] = useState(donationAmounts[0]);

    useEffect(() => {
        if (selectedDonationAmounts <= 15) {
            setFeeAmount((selectedDonationAmounts * 8));
        } else if (selectedDonationAmounts === 25) {
            setFeeAmount(150);
        } else {
            setFeeAmount(200);
        }
    }, [selectedDonationAmounts]);

    const handleDonationButtonClick = useCallback(
        donation => () => {
            setSelectedDonationAmounts(donation);
        },
        []
    );

    const handleChange = useCallback(
        ({ error: formError }) => {
            if (error) {
                return setError(formError.message);
            }
            if (error) {
                setError(null);
            }
        },
        [error]
    );

    const handleSubmit = useCallback(
        async event => {
            event.preventDefault();

            if (!stripe || !elements || isDisabled) {
                return;
            }
            setDisabledStatus(true);

            const cardElement = elements.getElement(CardNumberElement);
            const transferGroup = nanoid();
            const isDelayPayment = recipientStripeUserId === DEFAULT_STRIPE_ACC_ID;

            const {
                data: { client_secret: clientSecret },
            } = await createPaymentIntents({
                amount: selectedDonationAmounts,
                stripeUserId: isDelayPayment ? null : recipientStripeUserId,
                transferGroup,
                feeAmount,
            });

            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });
            if (result.error) {
                setError(result.error.message);
                return;
            }

            await createTransaction({
                uid: result.paymentIntent.id,
                payment: result.paymentIntent,
                recipientStripeUserId,
                isCompleted: false,
                isDelayPayment,
                transferGroup,
                recipientId,
                senderId,
            });
            const buttonLink = isDelayPayment
                ? getStripeConnectLink(recipientEmail, recipientId)
                : null;
            await updateUsersAfterPayment({
                pendingPayments: isDelayPayment
                    ? [
                          {
                              ...result.paymentIntent,
                              transferGroup,
                              feeAmount,
                          },
                      ]
                    : [],
                amount: selectedDonationAmounts,
                recipientId,
                senderId,
            });
            await addTotalDonation(postId, selectedDonationAmounts);
            await updateReview({
                uid: reviewId,
                alreadyPaid: true,
            });

            const emailTemplate = isDelayPayment
                ? getReceiveMoneyWithoutStripeAccountEmailTemplate(
                      recipientEmail,
                      recipientFirstName,
                      senderFirstName,
                      buttonLink
                  )
                : getReceiveMoneyEmailTemplate(recipientEmail, recipientFirstName, senderFirstName);

            await sendEmail(emailTemplate);
            setDisabledStatus(false);
            onToggle();
            onPaySuccess();
        },
        [
            selectedDonationAmounts,
            recipientStripeUserId,
            recipientFirstName,
            senderFirstName,
            recipientEmail,
            onPaySuccess,
            recipientId,
            isDisabled,
            elements,
            onToggle,
            senderId,
            stripe,
            postId,
        ]
    );

    return (
        <Modal isOpen={isOpen} toggle={onToggle} className="make-payment-modal">
            <ModalHeader toggle={onToggle}>
                <ModalLogo />
                <span className="ml-3">
                    <b>Make a payment to {recipientFirstName}</b>
                </span>
            </ModalHeader>
            <ModalBody className="p-5">
                <div className="add-card__container">
                    <div className="d-flex align-items-end">
                        <div className="align-items-baseline d-flex flex-column mr-2">
                            <label className="add-card__label">Card number</label>
                            <div className="add-card__card-input add-card__card-number">
                                <CardNumberElement
                                    style={cardInputStyles}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <div className="align-items-baseline d-flex flex-column mr-2">
                            <label className="add-card__label">Expiry</label>
                            <div className="add-card__card-input add-card__card-expiry">
                                <CardExpiryElement
                                    style={cardInputStyles}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <div className="align-items-baseline d-flex flex-column mr-5">
                            <label className="add-card__label">Security</label>
                            <div className="add-card__card-input add-card__card-cvc">
                                <CardCvcElement style={cardInputStyles} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="align-items-baseline d-flex flex-column">
                            <label className="add-card__label">Donation amount</label>
                            <div className="d-flex align-items-center">
                                {donationAmounts.map((d, index) => (
                                    <>
                                        <div
                                            key={d}
                                            className={classNames(
                                                'd-flex justify-content-center align-items-center',
                                                {
                                                    'make-payment__donation-button--active':
                                                        d === selectedDonationAmounts,
                                                    'make-payment__donation-button':
                                                        d !== selectedDonationAmounts,
                                                }
                                            )}
                                            onClick={handleDonationButtonClick(d)}
                                        >
                                            ${d}
                                        </div>
                                        <When condition={index !== donationAmounts.length - 1}>
                                            <div className="make-payment__donation-delimiter" />
                                        </When>
                                    </>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex mt-3" style={{ color: 'red' }}>
                        {error}
                    </div>
                </div>
                <div className="d-flex justify-content-end">
                    <div className="align-items-center d-flex flex-column pt-3">
                        <div
                            className="make-payment__pay-button d-flex justify-content-center align-items-center"
                            onClick={handleSubmit}
                        >
                            Pay ${selectedDonationAmounts}.00
                        </div>
                        <div className="d-flex align-items-center justify-content-end px-5 pt-3 make-payment__tips_description">
                            We charge a small fee of <b className="ml-1">${feeAmount / 100}</b>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

MakePaymentModal.propTypes = {
    postId: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    recipientFirstName: PropTypes.string.isRequired,
    recipientId: PropTypes.string.isRequired,
    recipientEmail: PropTypes.string.isRequired,
    reviewId: PropTypes.string.isRequired,
    recipientStripeUserId: PropTypes.string,
    onToggle: PropTypes.func.isRequired,
    onPaySuccess: PropTypes.func.isRequired,
};

MakePaymentModal.defaultProps = {
    recipientStripeUserId: DEFAULT_STRIPE_ACC_ID,
};

export default React.memo(MakePaymentModal);
